// The guest share page (`/s/<token>`) — the whole UI an unauthenticated visitor gets.
//
// It is NOT the panel: no nav, no device list, no farm context, and no /api/* call
// beyond resolving its own token. What it offers is one device's live screen with
// touch, the three system navigation buttons, and (when the share allows it) camera
// passthrough from the visitor's own phone/laptop camera into that device.
//
// Every restriction here is cosmetic by design — the wire-level gate lives in the
// panel (server/deviceShares.ts + the guest control filter in server/relay.ts). This
// file only decides what to draw.
import { StreamSession } from '../stream/stream-session';
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { startGuestCamera } from './guest-camera';
const navButtons = [
    { keycode: 4, labelKey: 'common.back', svg: '<svg viewBox="0 0 24 24"><path d="m15 5-7 7 7 7"/></svg>' },
    { keycode: 3, labelKey: 'share.nav_home', svg: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="7"/></svg>' },
    { keycode: 187, labelKey: 'share.nav_recent', svg: '<svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="1.5"/></svg>' },
];
const cameraOnSvg = '<svg viewBox="0 0 24 24"><path d="M4 8a2 2 0 0 1 2-2h1.5L9 4h6l1.5 2H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="3.5"/></svg>';
const cameraFlipSvg = '<svg viewBox="0 0 24 24"><path d="M4 8a2 2 0 0 1 2-2h1.5L9 4h6l1.5 2H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M9.5 13.5a2.5 2.5 0 0 1 4.3-1.7M14.5 12.5a2.5 2.5 0 0 1-4.3 1.7"/><path d="m13.4 11.2 1.1 1.3-1.5.5M10.6 15.8l-1.1-1.3 1.5-.5"/></svg>';
/** "42 min left" / "3 h 10 min left" — shown so a guest isn't cut off by surprise. */
function remainingText(expiresAt) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0)
        return t('share.expired');
    const min = Math.round(ms / 60000);
    if (min < 60)
        return t('share.min_left', { min });
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? t('share.h_min_left', { h, m }) : t('share.h_left', { h });
}
function fatal(message) {
    document.body.innerHTML = `
        <div class="guest-page guest-page--message">
            <div class="guest-message">
                <h1>${t('share.link_unavailable')}</h1>
                <p>${esc(message)}</p>
            </div>
        </div>
    `;
}
/** Boot the guest page for `token`. Called from the app entry before any panel code. */
export async function renderGuestShare(token) {
    let info;
    try {
        const res = await fetch(`/api/share/${encodeURIComponent(token)}`);
        if (!res.ok) {
            fatal((await res.json()).error ?? t('share.link_invalid'));
            return;
        }
        info = await res.json();
    }
    catch {
        fatal(t('share.server_unreachable'));
        return;
    }
    document.title = t('share.page_title', { device: info.deviceName });
    document.body.innerHTML = `
        <div class="guest-page">
            <header class="guest-header">
                <span class="guest-header__name">${esc(info.deviceName)}</span>
                <span class="guest-header__ttl" data-guest-ttl>${esc(remainingText(info.expiresAt))}</span>
            </header>
            <div class="guest-stage">
                <canvas class="guest-canvas" data-guest-canvas></canvas>
                <div class="guest-status" data-guest-status>${t('share.connecting')}</div>
            </div>
            <nav class="guest-nav">
                ${info.camera
        ? `<button type="button" class="guest-btn guest-btn--camera" data-guest-camera title="${t('share.share_camera')}">${cameraOnSvg}<span data-guest-camera-label>${t('share.camera')}</span></button>
                   <button type="button" class="guest-btn" data-guest-camera-flip title="${t('share.camera_facing_rear')}" aria-label="${t('share.camera_facing_rear')}">${cameraFlipSvg}</button>`
        : ''}
                <span class="guest-nav__spacer"></span>
                ${navButtons.map((b) => {
        const label = t(b.labelKey);
        return `<button type="button" class="guest-btn" data-keycode="${b.keycode}" title="${label}" aria-label="${label}">${b.svg}</button>`;
    }).join('')}
            </nav>
        </div>
    `;
    const canvas = document.querySelector('[data-guest-canvas]');
    const status = document.querySelector('[data-guest-status]');
    const ctx = canvas.getContext('2d');
    const setStatus = (text) => {
        status.textContent = text;
        status.style.display = text ? '' : 'none';
    };
    const session = new StreamSession(info.udid, canvas, ctx, 
    // StreamSession sizes the canvas from the metadata itself; the page only has to
    // drop the "connecting" overlay once frames are on the way.
    () => setStatus(''), () => setStatus(t('share.reconnecting')), (by) => setStatus(t('share.in_use_by', { by })), false, 
    // Guest mode: the token authorizes the socket, and pointer input makes the
    // screen usable with a finger — this page is usually opened on a phone.
    { shareToken: token, pointerInput: true });
    for (const btn of document.querySelectorAll('.guest-btn[data-keycode]')) {
        btn.addEventListener('click', () => session.sendKeycode(Number(btn.dataset['keycode'])));
    }
    // Countdown, refreshed once a minute. The socket is closed server-side at expiry —
    // this is only so the guest sees it coming.
    const ttlEl = document.querySelector('[data-guest-ttl]');
    setInterval(() => { ttlEl.textContent = remainingText(info.expiresAt); }, 60000);
    if (info.camera)
        wireCameraButton(token, info.udid);
}
/**
 * Camera passthrough: the on/off toggle plus the front/rear switch.
 *
 * The rear ("environment") camera is the default — the share link is opened on a phone
 * and what gets shown to the device is usually a document, not the guest's face. A
 * switch mid-stream restarts the uplink: the farm agent's encoder is started once per
 * session and cannot take a new track (see guest-camera.ts).
 */
function wireCameraButton(token, udid) {
    const btn = document.querySelector('[data-guest-camera]');
    const label = document.querySelector('[data-guest-camera-label]');
    const flipBtn = document.querySelector('[data-guest-camera-flip]');
    if (!btn || !label)
        return;
    let camera = null;
    let facing = 'environment';
    /** Transient banner over the screen — the only place a guest sees an error. */
    const flash = (message) => {
        const status = document.querySelector('[data-guest-status]');
        if (!status)
            return;
        status.textContent = message;
        status.style.display = '';
        setTimeout(() => { status.style.display = 'none'; }, 5000);
    };
    const syncFlipTitle = () => {
        if (!flipBtn)
            return;
        // The title says which camera is live, not what the button would switch to —
        // the guest needs to know what the device is being shown right now.
        const title = t(facing === 'environment' ? 'share.camera_facing_rear' : 'share.camera_facing_front');
        flipBtn.title = title;
        flipBtn.setAttribute('aria-label', title);
    };
    const start = async () => {
        btn.disabled = true;
        if (flipBtn)
            flipBtn.disabled = true;
        label.textContent = t('share.camera_starting');
        try {
            camera = await startGuestCamera(token, udid, (reason) => {
                // The uplink dropped (device refused it, link revoked, network) —
                // reflect it on the button and say why when we know.
                camera = null;
                btn.classList.remove('is-active');
                label.textContent = t('share.camera');
                if (reason)
                    flash(reason);
            }, facing);
            btn.classList.add('is-active');
            label.textContent = t('share.camera_streaming');
        }
        catch (e) {
            label.textContent = t('share.camera');
            flash(e.message);
        }
        finally {
            btn.disabled = false;
            if (flipBtn)
                flipBtn.disabled = false;
        }
    };
    const stop = () => {
        camera?.stop();
        camera = null;
        btn.classList.remove('is-active');
        label.textContent = t('share.camera');
    };
    syncFlipTitle();
    btn.addEventListener('click', () => {
        if (camera)
            stop();
        else
            void start();
    });
    flipBtn?.addEventListener('click', () => {
        facing = facing === 'environment' ? 'user' : 'environment';
        syncFlipTitle();
        if (!camera)
            return;
        stop();
        void start();
    });
}
